/**
 * AxelResearchDesk — FLEXX Academy CFD & Research Intelligence Panel
 *
 * Security fixes applied (per EMERGENT_AXEL_INTEL_HUB_BRIEF.md Step 1):
 *   ✅ AXEL_CONFIG block removed — no API keys in frontend
 *   ✅ Setup instructions comment removed
 *   ✅ callAxel() routes to POST /api/axel/research (never calls Anthropic directly)
 *   ✅ PMIRGate component removed — pmir_access controlled by user profile from backend
 *   ✅ API key warning banner removed
 *   ✅ SageMaster requires explicit user toggle (default OFF)
 */
import { useState, useRef, useEffect } from "react";
import { useIntelHub, useIntelHubOptional } from "../context/IntelHubContext";
import { LiveTriggerPanel } from "./AxelLiveTrigger";
import { runAxelJob } from "./axelJob";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Barlow+Condensed:wght@600;700;800&family=Barlow:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:#07090f; --surf:#0d1117; --border:#1c2030; --gold:#F5A623; --gdim:#8a5c0f;
    --green:#00d68f; --red:#ff4757; --blue:#38bdf8; --text:#c8d4e0; --muted:#4a5568;
    --white:#eef2f7; --mono:'IBM Plex Mono',monospace; --hd:'Barlow Condensed',sans-serif; --bd:'Barlow',sans-serif;
  }
  body { background:var(--bg); color:var(--text); font-family:var(--mono); }
  ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-track{background:var(--bg)} ::-webkit-scrollbar-thumb{background:var(--border);border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pdot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.3;transform:scale(0.6)}}
  .fu{animation:fadeUp 0.3s ease forwards}
  .tabx{transition:all 0.15s;cursor:pointer;font-family:var(--hd)}
  .tabx:hover{border-color:var(--gold) !important;color:var(--gold) !important}
  .runbtn{transition:all 0.2s;cursor:pointer;font-family:var(--hd)}
  .runbtn:not(:disabled):hover{background:var(--gold) !important;color:var(--bg) !important}
  .runbtn:disabled{opacity:0.35;cursor:not-allowed}
  .acbtn{transition:all 0.15s;cursor:pointer}
  .acbtn:hover{border-color:var(--gold) !important;color:var(--gold) !important}
  input:focus,textarea:focus{border-color:var(--gold) !important;outline:none}
  .ab h1{font-family:var(--hd);color:var(--gold);font-size:1rem;margin:16px 0 6px;text-transform:uppercase;letter-spacing:.05em}
  .ab h2{font-family:var(--hd);color:var(--white);font-size:.95rem;margin:14px 0 5px;letter-spacing:.03em}
  .ab h3{font-family:var(--hd);color:var(--text);font-size:.85rem;margin:12px 0 4px}
  .ab strong{color:var(--white)}
  .ab hr{border:none;border-top:1px solid var(--border);margin:14px 0}
  .ab p{margin-bottom:7px;line-height:1.7;font-size:.83rem}
  .ab .di{background:#0a1020;border:1px solid #2a1a00;border-left:3px solid var(--gold);border-radius:6px;padding:11px 13px;margin-top:18px;font-size:.74rem;color:var(--muted);line-height:1.65;font-family:var(--bd)}
`;

// Research modes
const R_MODES = [
  { id:"bias", label:"AXEL BIAS", icon:"⚡", color:"#F5A623", tag:"FLAGSHIP", ph:"e.g. NVDA, AAPL, EUR/USD",
    prompt:`You are AXEL, Flexx Academy AI Research Intelligence. Use web search NOW before answering.
Search: current price, latest news (7 days), analyst consensus, social sentiment, upcoming catalysts.

Output EXACTLY:
AXEL BIAS REPORT — [ASSET] | [DATE]
DIRECTIONAL BIAS: [BULLISH / BEARISH / NEUTRAL]
CONFIDENCE: [XX]%
TIMEFRAME: Short-term (1-5 days) | Medium-term (1-4 weeks)

## BIAS RATIONALE
4-5 bullets with real data, prices, headlines cited.

## KEY LEVELS
- Resistance: [levels]
- Support: [levels]
- Current Price: [live price]

## NEWS SUMMARY
3 most important recent headlines plus impact.

## SENTIMENT
- Analyst Consensus: [rating + avg target]
- Social Sentiment: [positive/negative/mixed]

## RISKS TO BIAS
2-3 bullets on what invalidates this view.

## AXEL VERDICT
2 sentences. Clear. Actionable for a trader.

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"tech", label:"Technical", icon:"📈", color:"#4ECDC4", tag:"MORGAN STANLEY", ph:"e.g. AAPL — watching breakout",
    prompt:`You are AXEL, Flexx Academy AI. Use web search for current price, recent price action, technical levels.

AXEL TECHNICAL ANALYSIS — [ASSET] | [DATE]
TECHNICAL BIAS: [BULLISH / BEARISH / NEUTRAL]

## TREND (Daily / Weekly / Monthly)
## KEY LEVELS (Resistance / Support / Current Price)
## INDICATORS: RSI, MACD, 50-Day MA, 200-Day MA, Bollinger Bands
## VOLUME: Confirming or contradicting?
## CHART PATTERN: Any identifiable patterns?
## TRADE SETUP: Entry Zone / Stop Loss / Target 1 (RR) / Target 2 (RR) / Invalidation

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"fund", label:"Fundamental", icon:"🏦", color:"#A78BFA", tag:"GOLDMAN SACHS", ph:"e.g. TSLA — margins concern",
    prompt:`You are AXEL, Flexx Academy AI. Search: latest earnings, revenue, analyst targets, valuation metrics.

AXEL FUNDAMENTAL ANALYSIS — [COMPANY] | [DATE]
INVESTMENT BIAS: [BULLISH / BEARISH / NEUTRAL]
CONVICTION: [LOW / MEDIUM / HIGH]

## BUSINESS SNAPSHOT (3 sentences)
## FINANCIAL HEALTH: Revenue TTM, Gross Margin, Net Margin, FCF, Debt/Equity
## VALUATION vs PEERS: P/E, P/S, EV/EBITDA — cheap/fair/expensive
## COMPETITIVE MOAT: Brand / Pricing Power / Switching Costs / Network Effects (1-10 each)
## ANALYST CONSENSUS: Avg target, buy/hold/sell split
## BULL vs BEAR (12-month targets with reasoning)
## AXEL VERDICT: Buy / Hold / Avoid

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"news", label:"News & Sentiment", icon:"📰", color:"#F97316", tag:"REAL-TIME", ph:"e.g. AMZN — what's moving it?",
    prompt:`You are AXEL, Flexx Academy AI. Run multiple searches: asset news today, analyst ratings, Twitter/Reddit sentiment, upcoming catalysts.

AXEL NEWS & SENTIMENT — [ASSET] | [DATE]
OVERALL SENTIMENT: [POSITIVE / NEGATIVE / MIXED]
NEWS IMPACT: [BULLISH / BEARISH / NEUTRAL]

## TOP HEADLINES (Last 7 Days) — 5-6 headlines with impact note
## ANALYST ACTIVITY: Recent upgrades/downgrades/target changes
## SOCIAL SENTIMENT: X/Twitter tone, Reddit mood, retail intensity
## INSTITUTIONAL SIGNALS: Notable buying/selling data
## UPCOMING CATALYSTS (Next 30 days)
## SIGNAL vs NOISE: What matters vs what to ignore
## AXEL VERDICT

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"macro", label:"Macro", icon:"🌐", color:"#34D399", tag:"TWO SIGMA", ph:"e.g. Gold, Oil, S&P500",
    prompt:`You are AXEL, Flexx Academy AI. Search: Fed policy, CPI, jobs, GDP, DXY, sector rotation, geopolitical risks.

AXEL MACRO IMPACT — [ASSET] | [DATE]
MACRO ENVIRONMENT: [RISK-ON / RISK-OFF / MIXED]
MACRO STANCE: [TAILWIND / HEADWIND / NEUTRAL] for this asset

## ECONOMIC DASHBOARD: Fed Policy / Inflation CPI / Jobs / GDP / DXY direction
## RATE IMPACT: How rates specifically affect this asset now
## MARKET CYCLE PHASE: Expansion/Peak/Contraction/Recovery
## GEOPOLITICAL FACTORS relevant to this asset
## 3-MONTH MACRO OUTLOOK: Tailwind or headwind and why

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"earn", label:"Earnings", icon:"📊", color:"#FB7185", tag:"JPMORGAN", ph:"e.g. NVDA — earnings next week",
    prompt:`You are AXEL, Flexx Academy AI. Search: earnings date, EPS/revenue estimates, last 6 quarters history, options implied move.

AXEL EARNINGS INTELLIGENCE — [COMPANY] | [DATE]
EARNINGS BIAS: [BEAT / MISS / IN-LINE likely]
PRE-EARNINGS POSITIONING: [BUY BEFORE / WAIT / AVOID]

## NEXT EARNINGS DATE
## WALL STREET ESTIMATES: EPS / Revenue / Whisper number
## TRACK RECORD (Last 6Q): Quarter, EPS Est vs Actual, Beat/Miss, Stock Reaction
## KEY METRICS TO WATCH: 3-5 numbers that will move the stock
## MANAGEMENT GUIDANCE REVIEW
## OPTIONS SIGNAL: Implied move, historical avg, calls/puts skew
## TRADE PLAYBOOK: Pre-earnings / Gap Up / Gap Down / Flat

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },
];

// CFD Desk modes — prompts as functions taking asset name
const CFD_MODES = [
  { id:"session", label:"Session Bias", icon:"⚡", color:"#F5A623",
    getPrompt: (asset) => `You are AXEL, Flexx Academy CFD Intelligence for MT5 and SageMaster traders. Use web search NOW.
Search: current ${asset} live price, today price action, news driving ${asset}${asset==="XAUUSD"?", DXY direction, US yields, gold safe haven demand":""} ${asset==="BTCUSD"?", BTC ETF flows, crypto sentiment, on-chain data, Bitcoin dominance":""}${asset==="USOIL"?", EIA crude inventory, OPEC news, WTI storage levels, US rig count":""}${asset==="UKOIL"?", Brent crude news, OPEC news, North Sea supply, Brent-WTI spread, geopolitical risk":""}.

AXEL SESSION BIAS — ${asset} | [DATE]
SESSION BIAS: [BULLISH / BEARISH / NEUTRAL]
CONFIDENCE: [XX]%

## LIVE PRICE CONTEXT
Current price, today range, vs yesterday close.

## TOP DRIVERS TODAY
3-4 bullets with real data on what is actually moving ${asset} right now.

## KEY LEVELS FOR THIS SESSION
- Strong Resistance: [price]
- Resistance 1: [price]
- Pivot: [price]
- Support 1: [price]
- Strong Support: [price]

## BIAS RATIONALE
3-4 specific bullets supporting the directional call.

## RISK EVENTS TODAY
Scheduled news, speeches, or data releases that could spike ${asset}.

## AXEL VERDICT
2 sentences. Clear directional call for a CFD trader on MT5.

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"mt5", label:"INTRADAY SWING - SETUP", icon:"📐", color:"#00d68f",
    getPrompt: (asset) => `You are AXEL, Flexx Academy MT5 Trade Setup Engine. Use web search NOW to get the current live price and recent price action for ${asset}. Pull key support, resistance, and technical levels from real data.

AXEL MT5 TRADE SETUP — ${asset} | [DATE] | CFD / FUTURES

TRADE DIRECTION: [BUY / SELL]
SETUP TYPE: [BREAKOUT / PULLBACK / REVERSAL / RANGE]
VALIDITY: [e.g. Valid for London plus NY session / Next 24-48hrs]
AXEL CONFIDENCE: [XX]% — your real conviction in this setup based on the confluence of technicals, market structure, and session context (output an actual number, e.g. 72%)

## MT5 ORDER PARAMETERS

Symbol: ${asset}
Direction: BUY or SELL
Entry Zone: [price to price]
Stop Loss: [price]
TP1: [price]
TP2: [price]
TP3: [price]

## DISTANCE BREAKDOWN
Reference price rule (CRITICAL):
- For a SELL setup, use ENTRY_HIGH as the reference price.
- For a BUY setup, use ENTRY_LOW as the reference price.
- Use the SAME reference price for SL distance, all TP distances, and RR — do NOT mix references.

Pip / point conventions:
- XAUUSD: 1 pip = 0.10 price move | 1 point = 0.01 price move
- BTCUSD: 1 pip = 1.00 price move | 1 point = 0.01 price move
- USOIL:  1 pip = 0.01 price move | 1 point = 0.001 price move
- UKOIL:  1 pip = 0.01 price move | 1 point = 0.001 price move  (Brent — same MT5 tick as WTI)

Formula:
- pipDistance   = |ref − target| / pipSize
- pointDistance = |ref − target| / pointSize

Format (ALWAYS show both, pips first then points in parentheses; pips 1 decimal if needed, points whole):
SL Distance: [N] pips ([N0] points)
TP1: [N] pips ([N0] points) | RR 1:[X]
TP2: [N] pips ([N0] points) | RR 1:[X]
TP3: [N] pips ([N0] points) | RR 1:[X]

NEVER label raw price difference as pips. NEVER mix points and pips.

## LOT SIZE GUIDE (1% Risk Per Trade)

$1,000 account — Max Loss $10 — Suggested [lots]
$3,000 account — Max Loss $30 — Suggested [lots]
$5,000 account — Max Loss $50 — Suggested [lots]
$10,000 account — Max Loss $100 — Suggested [lots]

## EXECUTION NOTES
Order Type: Market or Pending Limit
${asset==="XAUUSD"?"Gold CFD swaps apply for overnight holds — check your broker's swap rates. Typical spread 20-40 points during London/NY overlap.":asset==="BTCUSD"?"BTC CFD swap rates are higher than forex — factor into multi-day holds. Spreads widen during low liquidity — avoid entries near session close.":asset==="USOIL"?"WTI/USOIL CFD swaps vary by broker. Spreads widen ahead of EIA Crude Inventory data (Wednesdays 14:30 GMT). Best execution during US session (NYMEX hours).":asset==="UKOIL"?"Brent/UKOIL CFD swaps vary by broker. Spreads widen ahead of EIA/API data and OPEC meetings. Best execution during London open (ICE exchange) and London/NY overlap.":""}

## TRADE MANAGEMENT
Move SL to break even after TP1. Close 50% at TP1. Hold rest to TP2 or TP3.
Hard invalidation: [price level — exit immediately if hit]

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.

After the disclaimer, output this EXACT structured block with the real numbers from your analysis above (replace all placeholders with actual prices — no brackets):

---SIGNAL_START---
SYMBOL: ${asset}
DIRECTION: BUY
ENTRY_LOW: 0000.00
ENTRY_HIGH: 0000.00
STOP_LOSS: 0000.00
TP1: 0000.00
TP2: 0000.00
TP3: 0000.00
SETUP_TYPE: Brief setup description
VALIDITY: Session or time validity
LOT_1K: 0.00
LOT_3K: 0.00
LOT_5K: 0.00
LOT_10K: 0.00
POI_TYPE: Poi Bearish Retracement
INVALIDATION: 0000.00
TARGET_1: 0000.00
TARGET_2: 0000.00
TARGET_3: 0000.00
TARGET_4: 0000.00
TARGET_5: 0000.00
TARGET_6: 0000.00
TARGET_7: 0000.00
TARGET_8: 0000.00
TARGET_9: 0000.00
---SIGNAL_END---

For POI_TYPE use exactly one of: Poi Bearish Retracement / Poi Bullish Retracement / Poi Bearish Breakdown / Poi Bullish Breakout / Poi Bearish Reversal / Poi Bullish Reversal
For TARGET_1 through TARGET_9: if SELLS, targets go progressively lower from entry (spaced ~50-80 pips apart for XAUUSD, ~50-100 for BTCUSD, ~25-40 for USOIL/WTI, ~25-40 for UKOIL/Brent). If BUYS, targets go progressively higher. Fill all 9 with real price levels.
INVALIDATION is the price level where the trade is invalid (typically SL level).` },

  // AXEL Live Trigger — handled by a dedicated component (LiveTriggerPanel),
  // NOT CfdPanel. getPrompt is a no-op so the mode-tab map stays uniform.
  { id:"live", label:"LIVE TRIGGER SCALP", icon:"⚡", color:"#F5A623", getPrompt: () => "" },

  { id:"sm", label:"SageMaster", icon:"🔮", color:"#38bdf8",
    getPrompt: (asset) => `You are AXEL, Flexx Academy SageMaster AI Compatibility Analyst. Use web search to get current ${asset} price, recent price action, volatility, swing highs and lows.

AXEL x SAGEMASTER ANALYSIS — ${asset} | [DATE] | MT5 + SageMaster Ecosystem

SAGEMASTER SIGNAL: [BUY / SELL / WAIT]
MARKET CONDITION: [TRENDING / RANGING / VOLATILE]
Current Price: [live price from web search]
Asset Type: ${asset==="XAUUSD"?"Gold CFD (XAUUSD)":asset==="BTCUSD"?"Bitcoin CFD (BTCUSD)":asset==="USOIL"?"WTI Crude Oil CFD (USOIL)":asset==="UKOIL"?"Brent Crude Oil CFD (UKOIL)":asset}

## HELIX HARMONIC PATTERN SCAN
## AI GRID ANALYSIS
## AI DCA SETUP
## OMNI ASSIST SIGNAL
## SAGEMASTER RECOMMENDATION
## BEST TOOL FOR THIS SETUP

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"mtf", label:"MTF Bias", icon:"⏱", color:"#c084fc",
    getPrompt: (asset) => `You are AXEL, Flexx Academy Multi-Timeframe Bias Analyst. Use web search NOW for current ${asset} price and recent price action.

AXEL MULTI-TIMEFRAME BIAS — ${asset} | [DATE]
OVERALL SCALPER BIAS: [BULLISH / BEARISH / NEUTRAL]
MTF ALIGNMENT: [STRONG / PARTIAL / CONFLICTED]
CONFIDENCE: [XX]%

## D1 — DAILY (Higher Timeframe Anchor)
## H4 — 4-HOUR (Swing Direction)
## H1 — 1-HOUR (Intraday Trend)
## M15 — 15-MINUTE (Entry Timing)
## M5 — 5-MINUTE (Scalp Entries)

## MTF TRADE PLAN
## AXEL VERDICT

## SESSION TIMING GUIDE FOR ${asset} TODAY
${asset==="XAUUSD"?"London Open 08:00-10:00 GMT — highest Gold volatility (best breakouts). NY Open 13:00-15:00 GMT — second highest volume. London/NY Overlap 13:00-17:00 GMT — peak liquidity.":asset==="BTCUSD"?"US Market Hours 13:00-21:00 GMT — highest BTC volume aligned with US equities. Asian Session 00:00-08:00 GMT — lower volume, range plays. NY Close 20:00-22:00 GMT — watch for institutional breakouts.":asset==="USOIL"?"US Session 13:00-20:00 GMT — highest WTI volume. EIA Crude Inventory Wednesdays 14:30 GMT is the key event. London Open 08:00-10:00 GMT — secondary. Avoid Asian session unless OPEC breaking news.":asset==="UKOIL"?"London Open 07:00-10:00 GMT — primary Brent session, ICE exchange opens, European energy demand drives early moves. London/NY Overlap 13:00-17:00 GMT — peak liquidity, tightest spreads. Watch EIA data Wednesdays 14:30 GMT and OPEC statements.":""}

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },

  { id:"drivers", label:"News & Drivers", icon:"📰", color:"#F97316",
    getPrompt: (asset) => `You are AXEL, Flexx Academy News & Drivers Analyst. Use web search NOW to find the latest news and fundamental drivers for ${asset}.

AXEL NEWS & DRIVERS — ${asset} | [DATE]

## WHAT'S DRIVING ${asset} RIGHT NOW
3-4 real news items with source and impact direction.

## KEY CATALYSTS THIS WEEK
Scheduled events that could move ${asset}.

## INSTITUTIONAL FLOW & SENTIMENT
COT data, fund positioning, retail sentiment.${asset==="USOIL"?"\n\n## CUSHING STORAGE LEVELS\nCurrent WTI physical delivery hub inventory and trend — explain how it pressures or supports WTI prices.":""}${asset==="UKOIL"?"\n\n## BRENT-WTI SPREAD & NORTH SEA SUPPLY\nCurrent Brent-WTI spread (USD), trend, and what it signals. North Sea supply status — outages, maintenance, geopolitical risk affecting Brent specifically vs WTI.":""}

## AXEL MACRO VERDICT
How the macro backdrop affects ${asset} this week.

---
FLEXX ACADEMY DISCLAIMER: For educational purposes only. Not financial advice. Always manage your own risk.` },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function getBias(txt){
  const b=/BULLISH|BUY BIAS|LONG|TAILWIND/i.test(txt);
  const s=/BEARISH|SELL BIAS|SHORT|HEADWIND/i.test(txt);
  if(b&&!s) return "BULLISH"; if(s&&!b) return "BEARISH"; return "NEUTRAL";
}
function getConf(txt){ const m=txt.match(/CONFIDENCE[:\s]+(\d+)%/i); return m?+m[1]:null; }

// When the deterministic <DistanceBreakdown/> panel is shown, strip the AI's
// version of the section from the markdown response. The LLM's pip/point math
// is unreliable (it has labelled raw $1 price-diff as "1 pip" in production),
// while our JS helper uses the authoritative pip-size table.
//
// Matches BOTH the new "## DISTANCE BREAKDOWN" header and the legacy
// "## PIPS / POINTS BREAKDOWN" header — keeps stripping until the next "## "
// header or end of string.
function stripAiDistanceSection(raw){
  if(!raw) return "";
  return raw.replace(
    /(^|\n)#{1,3}\s*(DISTANCE BREAKDOWN|PIPS\s*\/\s*POINTS BREAKDOWN)\b[\s\S]*?(?=\n#{1,3}\s|\n*$)/gi,
    "$1",
  );
}

function renderMd(raw){
  if(!raw) return "";
  return raw
    .replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/^=+$/gm,'<hr/>')
    .replace(/^# (.+)$/gm,'<h1>$1</h1>')
    .replace(/^## (.+)$/gm,'<h2>$1</h2>')
    .replace(/^### (.+)$/gm,'<h3>$1</h3>')
    .replace(/\*\*([^*\n]+)\*\*/g,'<strong>$1</strong>')
    .replace(/^---$/gm,'<hr/>')
    .replace(/(FLEXX ACADEMY DISCLAIMER:[^\n]*(?:\n[^\n]+)*)/m,
      (m)=>`<div class="di">📌 ${m}</div>`)
    .replace(/^\|(.+)\|$/gm,(row)=>{
      const cells=row.split('|').filter(Boolean);
      if(cells.every(c=>/^[-:\s]+$/.test(c))) return '';
      return '<div style="display:flex;margin:1px 0">'+
        cells.map(c=>`<div style="flex:1;border:1px solid #1c2030;padding:5px 8px;font-size:.78rem">${c.trim()}</div>`).join('')+'</div>';
    })
    .replace(/^[-] (.+)$/gm,'<div style="display:flex;gap:7px;margin:3px 0;font-size:.83rem"><span style="color:#F5A623;flex-shrink:0">›</span><span>$1</span></div>')
    .replace(/\n/g,'<br/>');
}

function BiasChip({bias}){
  const map={BULLISH:{c:"#00d68f",bg:"#001a0f",l:"▲ BULLISH"},BEARISH:{c:"#ff4757",bg:"#1a000f",l:"▼ BEARISH"},NEUTRAL:{c:"#778ca3",bg:"#0d111a",l:"◆ NEUTRAL"}};
  const s=map[bias]||map.NEUTRAL;
  return <span style={{background:s.bg,border:`1px solid ${s.c}`,color:s.c,padding:"3px 10px",borderRadius:4,fontFamily:"var(--hd)",fontSize:".85rem",fontWeight:700,letterSpacing:".1em"}}>{s.l}</span>;
}
function Spinner(){ return <span style={{display:"inline-block",width:14,height:14,border:"2px solid #2a3040",borderTopColor:"#F5A623",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>; }
function LiveDot(){ return <span style={{display:"inline-flex",alignItems:"center",gap:4}}><span style={{width:6,height:6,borderRadius:"50%",background:"#00d68f",animation:"pdot 1.4s ease-in-out infinite",display:"inline-block"}}/><span style={{fontSize:".62rem",color:"#00d68f",letterSpacing:".1em",fontFamily:"var(--hd)"}}>LIVE</span></span>; }

function ResultView({ticker, bias, conf, result, modeLabel, onReset, onNew}){
  const [copied,setCopied]=useState(false);
  function copy(){ navigator.clipboard?.writeText(result);setCopied(true);setTimeout(()=>setCopied(false),2000); }
  return (
    <div className="fu">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:13,gap:10,flexWrap:"wrap"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}>
            <span style={{fontFamily:"var(--hd)",fontSize:"1.1rem",fontWeight:800,color:"var(--white)",letterSpacing:".04em"}}>{ticker}</span>
            {bias&&<BiasChip bias={bias}/>}
            {conf&&<span style={{fontSize:".7rem",color:"var(--muted)",fontFamily:"var(--bd)"}}>{conf}% confidence</span>}
          </div>
          <div style={{fontSize:".6rem",color:"var(--muted)",marginTop:4,letterSpacing:".1em",textTransform:"uppercase"}}>{modeLabel} · Flexx Academy AXEL · Live Research</div>
        </div>
        <div style={{display:"flex",gap:7}}>
          <button className="acbtn" onClick={copy} style={{padding:"6px 11px",background:"var(--surf)",border:"1px solid var(--border)",borderRadius:5,color:"var(--muted)",fontFamily:"var(--mono)",fontSize:".68rem"}}>{copied?"✓ COPIED":"📋 COPY"}</button>
          <button className="acbtn" onClick={onReset} style={{padding:"6px 11px",background:"var(--surf)",border:"1px solid var(--border)",borderRadius:5,color:"var(--muted)",fontFamily:"var(--mono)",fontSize:".68rem"}}>← NEW</button>
        </div>
      </div>
      {conf&&<div style={{marginBottom:13}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:".62rem",color:"var(--muted)"}}><span>AXEL CONFIDENCE</span><span style={{color:conf>70?"var(--green)":conf>40?"var(--gold)":"var(--red)"}}>{conf}%</span></div>
        <div style={{height:3,background:"var(--border)",borderRadius:2}}><div style={{height:"100%",borderRadius:2,width:`${conf}%`,background:conf>70?"var(--green)":conf>40?"var(--gold)":"var(--red)",transition:"width 1s ease"}}/></div>
      </div>}
      <div className="ab" style={{background:"var(--surf)",border:"1px solid var(--border)",borderRadius:10,padding:"20px 17px"}} dangerouslySetInnerHTML={{__html:renderMd(result)}}/>
      <div style={{marginTop:11,display:"flex",gap:8,flexWrap:"wrap"}}>
        <button className="runbtn" onClick={onNew} style={{flex:1,minWidth:130,padding:"10px",background:"transparent",border:"1px solid var(--border)",borderRadius:6,color:"var(--muted)",fontSize:".74rem",fontWeight:700,letterSpacing:".1em"}}>🔄 NEW ANALYSIS</button>
        <button className="runbtn" onClick={onReset} style={{flex:1,minWidth:130,padding:"10px",background:"transparent",border:"1px solid var(--border)",borderRadius:6,color:"var(--muted)",fontSize:".74rem",fontWeight:700,letterSpacing:".1em"}}>← CHANGE MODE</button>
      </div>
    </div>
  );
}

function LoadingView({ticker, phase, labels}){
  return (
    <div className="fu" style={{padding:"42px 20px",textAlign:"center"}}>
      <div style={{marginBottom:18}}><Spinner/></div>
      <div style={{fontFamily:"var(--hd)",fontSize:"1rem",fontWeight:700,color:"var(--white)",letterSpacing:".08em",marginBottom:8}}>
        {phase==="searching"?"SEARCHING LIVE DATA...":"SYNTHESIZING REPORT..."}
      </div>
      <div style={{fontSize:".73rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.9}}>
        {phase==="searching"?`Scanning live data for ${ticker}`:"Cross-referencing sources..."}
      </div>
      <div style={{marginTop:16,display:"flex",justifyContent:"center",gap:6,flexWrap:"wrap"}}>
        {labels.map((s,i)=>(
          <div key={s} style={{padding:"3px 9px",background:"var(--surf)",border:"1px solid var(--border)",borderRadius:4,fontSize:".6rem",color:"var(--muted)",fontFamily:"var(--hd)",animation:`pdot 1.4s ease-in-out ${i*.18}s infinite`}}>{s}</div>
        ))}
      </div>
    </div>
  );
}

/**
 * callAxel — backend proxy via the async-job system. NEVER calls Anthropic
 * directly. Starts a research job and polls until done, so a slow/CPU-throttled
 * production origin can never trip a gateway 504.
 */
async function callAxel(systemPrompt, userMsg, asset){
  const body = { system_prompt: systemPrompt, user_message: userMsg };
  if(asset) body.asset = asset;
  const result = await runAxelJob(
    "/api/axel/research",
    body,
  );
  return result || {};
}

// ── Research Panel ────────────────────────────────────────────────────────────
function ResearchPanel({mode}){
  const [ticker,setTicker]=useState("");
  const [ctx,setCtx]=useState("");
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState("");
  const [bias,setBias]=useState(null);
  const [conf,setConf]=useState(null);
  const [err,setErr]=useState("");
  const [phase,setPhase]=useState("");
  const ref=useRef(null);
  const inRef=useRef(null);

  async function run(){
    if(!ticker.trim()||loading) return;
    setLoading(true);setResult("");setBias(null);setConf(null);setErr("");setPhase("searching");
    setTimeout(()=>setPhase("synthesizing"),4500);
    try{
      const res=await callAxel(mode.prompt,`Asset: ${ticker.trim().toUpperCase()}${ctx.trim()?`\nContext: ${ctx.trim()}`:""}. Search for live data first.`);
      const txt=res?.text;
      if(txt){ setResult(txt);setBias(getBias(txt));setConf(getConf(txt));setTimeout(()=>ref.current?.scrollIntoView({behavior:"smooth",block:"start"}),100); }
      else setErr("No response. Try again.");
    }catch(e){ setErr(e.message||"Connection error. Please retry."); }
    setLoading(false);setPhase("");
  }

  function reset(){ setResult("");setBias(null);setConf(null);setErr("");setTicker("");setCtx("");setTimeout(()=>inRef.current?.focus(),50); }

  if(loading) return <LoadingView ticker={ticker.toUpperCase()} phase={phase} labels={["📰 NEWS","💹 PRICES","👔 ANALYSTS","💬 SENTIMENT","📊 CHARTS"]}/>;
  if(result) return <ResultView ticker={ticker.toUpperCase()} bias={bias} conf={conf} result={result} modeLabel={`${mode.icon} ${mode.label}`} onReset={reset} onNew={()=>{setResult("");setBias(null);setConf(null);}}/>;

  return (
    <div ref={ref} className="fu">
      {err&&<div style={{padding:"11px 13px",background:"#1a0810",border:"1px solid #5a1030",borderRadius:7,marginBottom:13,color:"#ff6b88",fontSize:".8rem"}}>{err}</div>}
      <div style={{marginBottom:6,fontSize:".63rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase"}}>Asset / Ticker</div>
      <input ref={inRef} value={ticker} onChange={e=>setTicker(e.target.value)} onKeyDown={e=>e.key==="Enter"&&run()} placeholder={mode.ph}
        style={{width:"100%",padding:"12px 13px",marginBottom:11,background:"var(--surf)",border:"1.5px solid var(--border)",borderRadius:7,color:mode.color,fontFamily:"var(--mono)",fontSize:".95rem",letterSpacing:".04em"}}/>
      <div style={{marginBottom:6,fontSize:".63rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase"}}>Context <span style={{color:"#2a3040"}}>(optional)</span></div>
      <textarea value={ctx} onChange={e=>setCtx(e.target.value)} placeholder="Your position, timeframe, specific questions..." rows={2}
        style={{width:"100%",padding:"10px 13px",marginBottom:16,background:"var(--surf)",border:"1.5px solid var(--border)",borderRadius:7,color:"var(--text)",fontFamily:"var(--bd)",fontSize:".83rem",resize:"none",lineHeight:1.65}}/>
      <button className="runbtn" onClick={run} disabled={!ticker.trim()} data-testid="research-run-btn"
        style={{width:"100%",padding:"14px",background:"transparent",border:`1.5px solid ${ticker.trim()?mode.color:"var(--border)"}`,borderRadius:7,color:ticker.trim()?mode.color:"var(--muted)",fontSize:".95rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase"}}>
        ⚡ RUN {mode.label}
      </button>
      <div style={{marginTop:8,textAlign:"center",fontSize:".62rem",color:"var(--muted)",fontFamily:"var(--bd)"}}>AXEL searches live data before every analysis</div>
    </div>
  );
}

// ── Signal Helpers ────────────────────────────────────────────────────────────
function parseSignal(txt){
  const m=txt.match(/---SIGNAL_START---([\s\S]*?)---SIGNAL_END---/);
  if(!m) return null;
  const obj={};
  m[1].trim().split('\n').forEach(line=>{
    const idx=line.indexOf(':');
    if(idx>-1){ obj[line.slice(0,idx).trim()]=line.slice(idx+1).trim(); }
  });
  return obj.SYMBOL&&obj.DIRECTION?obj:null;
}

// ── Distance helpers (pip + point, single authoritative reference price) ─────
// Per-symbol convention:
//   XAUUSD: 1 pip = 0.10  | 1 point = 0.01
//   BTCUSD: 1 pip = 1.00  | 1 point = 0.01
//   USOIL : 1 pip = 0.01  | 1 point = 0.001
//   UKOIL : 1 pip = 0.01  | 1 point = 0.001  (Brent — same MT5 tick as WTI)
// Reference price rule:
//   - actual executed entry if provided
//   - else SELL → ENTRY_HIGH (conservative worst-fill above market)
//   - else BUY  → ENTRY_LOW  (conservative worst-fill below market)
// The SAME reference is used for SL, every TP, and the RR ratio.
const PIP_POINT_SIZE = {
  XAUUSD: { pip: 0.10, point: 0.01  },
  BTCUSD: { pip: 1.00, point: 0.01  },
  USOIL : { pip: 0.01, point: 0.001 },
  UKOIL : { pip: 0.01, point: 0.001 },
};

function fmtPips(n){
  if (!Number.isFinite(n)) return "—";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? `${r}` : r.toFixed(1);
}
function fmtPoints(n){
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)}`;
}
function distancePair(symbol, a, b){
  const cfg = PIP_POINT_SIZE[symbol];
  if (!cfg || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  const diff = Math.abs(a - b);
  return { pips: diff / cfg.pip, points: diff / cfg.point, diff };
}
function fmtDist(d){ return d ? `${fmtPips(d.pips)} pips (${fmtPoints(d.points)} points)` : "—"; }

function computeBreakdown(sig, executedEntry = null){
  if (!sig) return null;
  const symbol = sig.SYMBOL;
  if (!PIP_POINT_SIZE[symbol]) return null;
  const direction = String(sig.DIRECTION || "").toUpperCase();
  const entryLow  = parseFloat(sig.ENTRY_LOW);
  const entryHigh = parseFloat(sig.ENTRY_HIGH);
  const sl  = parseFloat(sig.STOP_LOSS);
  const tp1 = parseFloat(sig.TP1);
  const tp2 = parseFloat(sig.TP2);
  const tp3 = parseFloat(sig.TP3);

  let ref;
  let refLabel;
  if (Number.isFinite(executedEntry) && executedEntry > 0) {
    ref = executedEntry; refLabel = "executed entry";
  } else if (direction === "SELL") {
    ref = Number.isFinite(entryHigh) ? entryHigh : entryLow;
    refLabel = Number.isFinite(entryHigh) ? "ENTRY_HIGH (SELL)" : "ENTRY_LOW";
  } else {
    ref = Number.isFinite(entryLow) ? entryLow : entryHigh;
    refLabel = Number.isFinite(entryLow) ? "ENTRY_LOW (BUY)" : "ENTRY_HIGH";
  }

  const slD  = distancePair(symbol, ref, sl);
  const t1D  = distancePair(symbol, ref, tp1);
  const t2D  = distancePair(symbol, ref, tp2);
  const t3D  = distancePair(symbol, ref, tp3);
  const rr = (tp) => (slD && tp && slD.pips > 0) ? (tp.pips / slD.pips) : null;
  return {
    symbol, direction, reference: ref, referenceLabel: refLabel,
    sl: slD, tp1: t1D, tp2: t2D, tp3: t3D,
    rr1: rr(t1D), rr2: rr(t2D), rr3: rr(t3D),
  };
}

function DistanceBreakdown({ sig, executedEntry = null }){
  const b = computeBreakdown(sig, executedEntry);
  if (!b) return null;
  const Row = ({ label, dist, rr, color }) => (
    <div data-testid={`dist-row-${label.toLowerCase().replace(/\s/g,'-')}`} style={{
      display:"grid", gridTemplateColumns:"96px 1fr 110px", gap:10, alignItems:"center",
      padding:"7px 11px", borderRadius:6,
      background:"#020608", border:"1px solid #1c2030", borderLeft:`3px solid ${color}`,
    }}>
      <span style={{color, fontFamily:"var(--hd)", fontSize:".72rem", letterSpacing:".08em", fontWeight:700}}>{label}</span>
      <span style={{color:"var(--text)", fontFamily:"var(--mono)", fontSize:".82rem"}}>{fmtDist(dist)}</span>
      <span style={{color:"var(--muted)", fontFamily:"var(--mono)", fontSize:".74rem", textAlign:"right"}}>
        {rr != null ? `RR 1:${rr.toFixed(2)}` : ""}
      </span>
    </div>
  );
  return (
    <div data-testid="distance-breakdown" style={{
      padding:"12px 13px", background:"#020c04", border:"1px solid #0d3320", borderRadius:8,
    }}>
      <div style={{
        display:"flex", justifyContent:"space-between", alignItems:"baseline", gap:8, flexWrap:"wrap",
        marginBottom:9,
      }}>
        <div style={{fontFamily:"var(--hd)", fontSize:".78rem", fontWeight:800, color:"#00d68f", letterSpacing:".1em"}}>
          📏 DISTANCE BREAKDOWN
        </div>
        <div style={{fontSize:".64rem", color:"var(--muted)", fontFamily:"var(--mono)"}}>
          Reference: {Number.isFinite(b.reference) ? b.reference : "—"} <span style={{opacity:.6}}>({b.referenceLabel})</span>
        </div>
      </div>
      <div style={{display:"grid", gap:4}}>
        <Row label="SL"  dist={b.sl}  rr={null}   color="#ff4757" />
        <Row label="TP1" dist={b.tp1} rr={b.rr1}  color="#00d68f" />
        <Row label="TP2" dist={b.tp2} rr={b.rr2}  color="#00d68f" />
        <Row label="TP3" dist={b.tp3} rr={b.rr3}  color="#00d68f" />
      </div>
      <div style={{marginTop:8, fontSize:".62rem", color:"var(--muted)", fontFamily:"var(--bd)", lineHeight:1.6}}>
        XAUUSD: 1 pip = 0.10 · 1 point = 0.01 — MT5 displays points; pips are the trader convention.
      </div>
    </div>
  );
}

function formatTelegram(sig){
  const dir=sig.DIRECTION==="BUY"?"🟢 BUY":"🔴 SELL";
  const date=new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
  const b = computeBreakdown(sig);
  const distLine = (label, d, rr) => `${label}: ${fmtDist(d)}${rr!=null?` | RR 1:${rr.toFixed(2)}`:""}`;
  const distBlock = b ? `
📏 *DISTANCE BREAKDOWN* (ref: ${b.reference} · ${b.referenceLabel})
${distLine("SL", b.sl, null)}
${distLine("TP1", b.tp1, b.rr1)}
${distLine("TP2", b.tp2, b.rr2)}
${distLine("TP3", b.tp3, b.rr3)}
` : "";
  return `🔔 *FLEXX ACADEMY — AXEL SIGNAL*
━━━━━━━━━━━━━━━━━━━━━━━
📊 *${sig.SYMBOL}* | ${dir} | CFD/Futures
📅 ${date}
⚡ ${sig.SETUP_TYPE||"AI-Generated Setup"}
━━━━━━━━━━━━━━━━━━━━━━━

🎯 *ENTRY ZONE:* ${sig.ENTRY_LOW} – ${sig.ENTRY_HIGH}
🛑 *STOP LOSS:* ${sig.STOP_LOSS}
✅ *TP1:* ${sig.TP1}
✅ *TP2:* ${sig.TP2}
✅ *TP3:* ${sig.TP3}
${distBlock}
📐 *LOT SIZE GUIDE (1% Risk)*
$1,000 → ${sig.LOT_1K||"—"} lots
$3,000 → ${sig.LOT_3K||"—"} lots
$5,000 → ${sig.LOT_5K||"—"} lots
$10,000 → ${sig.LOT_10K||"—"} lots

⚙️ *TRADE MANAGEMENT*
• Move SL to break-even after TP1 hits
• Close 50% position at TP1
• Trail remaining to TP2 then TP3
• Validity: ${sig.VALIDITY||"Current session"}

━━━━━━━━━━━━━━━━━━━━━━━
📌 _For educational purposes only. Not financial advice. Always manage your own risk._
🎓 *Flexx Academy* | flexx-academy.com
━━━━━━━━━━━━━━━━━━━━━━━`;
}

function formatPMIR(sig){
  const isSell = sig.DIRECTION==="SELL";
  const label = isSell ? "S" : "R";
  const targets = [
    sig.TARGET_1,sig.TARGET_2,sig.TARGET_3,sig.TARGET_4,sig.TARGET_5,
    sig.TARGET_6,sig.TARGET_7,sig.TARGET_8,sig.TARGET_9
  ].filter(Boolean);
  const targetLines = targets.map((t,i)=>`${label}${i+1} ${t}`).join("\n");
  const date = new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"});
  return `📊 PMIR TRADE IDEA — ${sig.SYMBOL}
${date}
(FOR TRADE IDEA ONLY)
━━━━━━━━━━━━━━━━━━━━
${sig.POI_TYPE||sig.SETUP_TYPE}
${sig.ENTRY_LOW}-${sig.ENTRY_HIGH}
Invalidation- ${sig.INVALIDATION||sig.STOP_LOSS}

${targetLines}
━━━━━━━━━━━━━━━━━━━━
🔐 PMIR Members Only
For educational purposes only. Not financial advice. Trade at your own risk.
🎓 Flexx Academy | PMIR`;
}

// ── Trade Chart ───────────────────────────────────────────────────────────────
function TradeChart({sig}){
  if(!sig||!sig.ENTRY_LOW) return null;
  const isSell = sig.DIRECTION==="SELL";
  const targets = [sig.TARGET_1,sig.TARGET_2,sig.TARGET_3,sig.TARGET_4,sig.TARGET_5,
    sig.TARGET_6,sig.TARGET_7,sig.TARGET_8,sig.TARGET_9]
    .filter(Boolean).map(Number).filter(n=>!isNaN(n));
  if(targets.length===0) return null;
  const label = isSell ? "S" : "R";
  const sl = parseFloat(sig.STOP_LOSS);
  const allPrices = [sl, parseFloat(sig.ENTRY_LOW), parseFloat(sig.ENTRY_HIGH), ...targets];
  const minP = Math.min(...allPrices);
  const maxP = Math.max(...allPrices);
  const range = maxP - minP || 1;
  const W=340, H=320, PL=54, PR=14, PT=16, PB=16;
  const chartH = H-PT-PB;
  const chartW = W-PL-PR;
  function py(price){ return PT + (isSell ? (price-minP)/range : (maxP-price)/range)*chartH; }
  const entryLow = parseFloat(sig.ENTRY_LOW);
  const entryHigh = parseFloat(sig.ENTRY_HIGH);
  const entryY1 = py(Math.max(entryLow,entryHigh));
  const entryY2 = py(Math.min(entryLow,entryHigh));
  const fmt = (p)=>{const n=parseFloat(p);if(isNaN(n)) return p;return n>=1000?n.toFixed(0):n.toFixed(2);};
  return(
    <div style={{background:"#07090f",border:"1px solid #1c2030",borderRadius:10,padding:"14px 10px 10px",marginBottom:14}}>
      <div style={{fontFamily:"var(--hd)",fontSize:".75rem",fontWeight:700,color:"#F5A623",letterSpacing:".1em",marginBottom:8,paddingLeft:4}}>
        ⚡ TRADE STRUCTURE — {sig.SYMBOL} {sig.DIRECTION}
      </div>
      <svg width={W} height={H} style={{display:"block",maxWidth:"100%"}}>
        {[0,0.25,0.5,0.75,1].map(t=>{const y=PT+t*chartH;return <line key={t} x1={PL} y1={y} x2={W-PR} y2={y} stroke="#1c2030" strokeWidth="1"/>;}) }
        <rect x={PL} y={isSell?py(sl)-1:py(sl)} width={chartW} height={Math.abs(py(sl)-py(isSell?sl+range*0.01:sl-range*0.01))||4} fill="#ff475720" stroke="none"/>
        <line x1={PL} y1={py(sl)} x2={W-PR} y2={py(sl)} stroke="#ff4757" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={PL-4} y={py(sl)+4} textAnchor="end" fill="#ff4757" fontSize="9" fontFamily="IBM Plex Mono">{fmt(sl)}</text>
        <text x={W-PR+3} y={py(sl)+4} fill="#ff475799" fontSize="8" fontFamily="IBM Plex Mono">SL</text>
        <rect x={PL} y={Math.min(entryY1,entryY2)} width={chartW} height={Math.abs(entryY2-entryY1)||3} fill="#F5A62330" stroke="#F5A62360" strokeWidth="1"/>
        <text x={PL-4} y={Math.min(entryY1,entryY2)+10} textAnchor="end" fill="#F5A623" fontSize="9" fontFamily="IBM Plex Mono">{fmt(sig.ENTRY_HIGH)}</text>
        <text x={PL-4} y={Math.max(entryY1,entryY2)+4} textAnchor="end" fill="#F5A62399" fontSize="9" fontFamily="IBM Plex Mono">{fmt(sig.ENTRY_LOW)}</text>
        <text x={W-PR+3} y={(Math.min(entryY1,entryY2)+Math.max(entryY1,entryY2))/2+4} fill="#F5A62399" fontSize="8" fontFamily="IBM Plex Mono">POI</text>
        {targets.map((t,i)=>{const y=py(t);const alpha=Math.round(255*(1-i*0.08)).toString(16).padStart(2,'0');const col=`#00d68f${alpha}`;return(
          <g key={i}>
            <line x1={PL} y1={y} x2={W-PR} y2={y} stroke={col} strokeWidth={i===0?1.5:1} strokeDasharray={i===0?"none":"3,3"}/>
            <text x={PL-4} y={y+4} textAnchor="end" fill={col} fontSize="9" fontFamily="IBM Plex Mono">{fmt(t)}</text>
            <text x={W-PR+3} y={y+4} fill={col} fontSize="8" fontFamily="IBM Plex Mono">{label}{i+1}</text>
          </g>
        );})}
        <line x1={PL} y1={PT} x2={PL} y2={H-PB} stroke="#2a3040" strokeWidth="1"/>
        <text x={PL+chartW/2} y={PT+12} textAnchor="middle" fill={isSell?"#ff4757":"#00d68f"} fontSize="11" fontFamily="Barlow Condensed" fontWeight="700" letterSpacing="2">
          {isSell?"▼ SELL — TARGETS BELOW":"▲ BUY — TARGETS ABOVE"}
        </text>
      </svg>
      <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:4,fontSize:".65rem",fontFamily:"var(--bd)",color:"var(--muted)"}}>
        <span><span style={{color:"#ff4757"}}>—</span> Stop Loss</span>
        <span><span style={{color:"#F5A623"}}>█</span> Entry Zone</span>
        <span><span style={{color:"#00d68f"}}>—</span> Targets</span>
      </div>
    </div>
  );
}

// ── Execute Modal ─────────────────────────────────────────────────────────────
function ExecuteModal({sig, asset, onClose}){
  const [step,setStep]=useState(1);
  const [agreed,setAgreed]=useState(false);
  const [connType,setConnType]=useState("sagemaster");
  const [endpoint,setEndpoint]=useState("");
  const [apiKey,setApiKey]=useState("");
  const [riskPct,setRiskPct]=useState("1");
  const [executing,setExecuting]=useState(false);
  const [execResult,setExecResult]=useState(null);

  const payload={
    symbol:sig.SYMBOL, action:sig.DIRECTION,
    entry_low:parseFloat(sig.ENTRY_LOW), entry_high:parseFloat(sig.ENTRY_HIGH),
    stop_loss:parseFloat(sig.STOP_LOSS),
    tp1:parseFloat(sig.TP1), tp2:parseFloat(sig.TP2), tp3:parseFloat(sig.TP3),
    risk_percent:parseFloat(riskPct),
    setup_type:sig.SETUP_TYPE,
    comment:"AXEL-Flexx-Signal",
    magic_number:20250520,
    source:"AXEL_Flexx_Academy",
    disclaimer:"Educational_Signal_Only_Not_Financial_Advice"
  };

  async function execute(){
    setExecuting(true);
    const url = endpoint.trim() || (connType==="sagemaster"?"https://api.sagemaster.com/signal":"");
    try{
      const res=await fetch(url,{method:"POST",headers:{"Content-Type":"application/json","X-API-KEY":apiKey},body:JSON.stringify(payload)});
      const data=await res.json().catch(()=>({status:"sent"}));
      setExecResult({ok:res.ok,msg:res.ok?"Trade signal sent successfully.":"Server returned error: "+res.status,data});
    }catch(e){
      setExecResult({ok:false,msg:"Could not reach endpoint. Check your URL: "+e.message});
    }
    setExecuting(false);setStep(3);
  }

  const overlay={position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"};
  const box={background:"#0d1117",border:"1px solid #2a3040",borderRadius:12,maxWidth:480,width:"100%",maxHeight:"90vh",overflowY:"auto",padding:"24px 20px"};

  return(
    <div style={overlay} onClick={e=>{if(e.target===e.currentTarget)onClose()}}>
      <div style={box}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontFamily:"var(--hd)",fontSize:"1rem",fontWeight:800,color:"var(--white)",letterSpacing:".04em"}}>
            {step===1?"⚠️ Risk Acknowledgment":step===2?"🔗 Connect & Execute":"📡 Execution Result"}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:"1.1rem"}}>✕</button>
        </div>

        {step===1&&<>
          <div style={{background:"#1a0a00",border:"1px solid #5a2a00",borderLeft:"3px solid #F5A623",borderRadius:8,padding:"14px 15px",marginBottom:18}}>
            <div style={{fontFamily:"var(--hd)",fontSize:".85rem",fontWeight:700,color:"#F5A623",marginBottom:8,letterSpacing:".06em"}}>IMPORTANT LEGAL DISCLAIMER</div>
            <div style={{fontSize:".75rem",color:"#aaa",lineHeight:1.8,fontFamily:"var(--bd)"}}>
              <b style={{color:"var(--white)"}}>Flexx Academy and AXEL AI are NOT licensed financial advisors, brokers, or fund managers.</b><br/><br/>
              By proceeding, you confirm this signal is <b style={{color:"var(--white)"}}>AI-generated for educational purposes only</b> and does not constitute financial advice.
              You are <b style={{color:"var(--white)"}}>solely responsible</b> for all trading decisions and financial outcomes.
              CFD and futures trading involves <b style={{color:"var(--white)"}}>significant risk of loss</b>.
            </div>
          </div>
          <div style={{background:"#020c04",border:"1px solid #0d3320",borderLeft:"3px solid #00d68f",borderRadius:8,padding:"12px 14px",marginBottom:18,fontFamily:"var(--mono)",fontSize:".78rem",lineHeight:2}}>
            <div style={{fontFamily:"var(--hd)",fontSize:".8rem",fontWeight:700,color:"#00d68f",marginBottom:6}}>SIGNAL PREVIEW — {asset}</div>
            <div><span style={{color:"var(--muted)"}}>Direction:</span> <b style={{color:sig.DIRECTION==="BUY"?"#00d68f":"#ff4757"}}>{sig.DIRECTION}</b></div>
            <div><span style={{color:"var(--muted)"}}>Entry:</span> {sig.ENTRY_LOW} – {sig.ENTRY_HIGH}</div>
            <div><span style={{color:"var(--muted)"}}>Stop Loss:</span> {sig.STOP_LOSS}</div>
            <div><span style={{color:"var(--muted)"}}>TP1 / TP2 / TP3:</span> {sig.TP1} / {sig.TP2} / {sig.TP3}</div>
          </div>
          <label style={{display:"flex",gap:10,alignItems:"flex-start",cursor:"pointer",marginBottom:18}}>
            <input type="checkbox" checked={agreed} onChange={e=>setAgreed(e.target.checked)} style={{marginTop:2,width:16,height:16,accentColor:"#F5A623",flexShrink:0}}/>
            <span style={{fontSize:".75rem",color:"var(--text)",fontFamily:"var(--bd)",lineHeight:1.6}}>I have read and fully understand the disclaimer. I accept sole responsibility for this trade. I confirm this is for educational purposes only.</span>
          </label>
          <button onClick={()=>setStep(2)} disabled={!agreed}
            style={{width:"100%",padding:"13px",background:agreed?"transparent":"var(--border)",border:`1.5px solid ${agreed?"#00d68f":"var(--border)"}`,borderRadius:7,color:agreed?"#00d68f":"var(--muted)",fontFamily:"var(--hd)",fontSize:".9rem",fontWeight:700,letterSpacing:".1em",cursor:agreed?"pointer":"not-allowed",transition:"all .2s"}}>
            I AGREE — PROCEED TO CONNECT →
          </button>
        </>}

        {step===2&&<>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:8}}>Connection Type</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[{id:"sagemaster",label:"🔮 SageMaster Magic Terminal",sub:"Connect via SageMaster API Key"},{id:"mt5bridge",label:"📐 MT5 REST Bridge",sub:"Local or cloud EA bridge"},{id:"webhook",label:"🔗 Custom Webhook",sub:"n8n, Zapier, Make.com, or any REST endpoint"}].map(t=>(
                <label key={t.id} style={{display:"flex",gap:10,alignItems:"center",padding:"10px 12px",border:`1px solid ${connType===t.id?"#00d68f":"var(--border)"}`,borderRadius:7,cursor:"pointer",background:connType===t.id?"#020c0a":"var(--surf)"}}>
                  <input type="radio" name="conn" value={t.id} checked={connType===t.id} onChange={()=>setConnType(t.id)} style={{accentColor:"#00d68f"}}/>
                  <div><div style={{fontFamily:"var(--hd)",fontSize:".85rem",fontWeight:700,color:connType===t.id?"#00d68f":"var(--white)"}}>{t.label}</div><div style={{fontSize:".68rem",color:"var(--muted)",fontFamily:"var(--bd)"}}>{t.sub}</div></div>
                </label>
              ))}
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:6}}>Endpoint URL</div>
            <input value={endpoint} onChange={e=>setEndpoint(e.target.value)} placeholder="https://your-endpoint.com/signal"
              style={{width:"100%",padding:"10px 12px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:7,color:"var(--text)",fontFamily:"var(--mono)",fontSize:".78rem"}}
              onFocus={e=>e.target.style.borderColor="#00d68f"} onBlur={e=>e.target.style.borderColor="var(--border)"}/>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:6}}>API Key / Token</div>
            <input type="password" value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="Your API key or bearer token"
              style={{width:"100%",padding:"10px 12px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:7,color:"var(--text)",fontFamily:"var(--mono)",fontSize:".78rem"}}
              onFocus={e=>e.target.style.borderColor="#00d68f"} onBlur={e=>e.target.style.borderColor="var(--border)"}/>
          </div>
          <div style={{marginBottom:18}}>
            <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:6}}>Risk Per Trade</div>
            <div style={{display:"flex",gap:7}}>
              {["0.5","1","1.5","2"].map(r=>(
                <button key={r} onClick={()=>setRiskPct(r)} style={{flex:1,padding:"8px",border:`1px solid ${riskPct===r?"#00d68f":"var(--border)"}`,borderRadius:6,background:riskPct===r?"#020c0a":"var(--surf)",color:riskPct===r?"#00d68f":"var(--muted)",fontFamily:"var(--hd)",fontSize:".82rem",fontWeight:700,cursor:"pointer"}}>{r}%</button>
              ))}
            </div>
          </div>
          <div style={{background:"#020608",border:"1px solid #1c2030",borderRadius:7,padding:"10px 12px",marginBottom:16,fontSize:".68rem",color:"#4a8a6a",fontFamily:"var(--mono)",lineHeight:1.8,overflow:"auto",maxHeight:130}}>
            {JSON.stringify({...payload,risk_percent:parseFloat(riskPct)},null,2)}
          </div>
          <button onClick={execute} disabled={!endpoint.trim()||executing}
            style={{width:"100%",padding:"13px",background:!endpoint.trim()?"var(--border)":"transparent",border:`1.5px solid ${!endpoint.trim()?"var(--border)":"#00d68f"}`,borderRadius:7,color:!endpoint.trim()?"var(--muted)":"#00d68f",fontFamily:"var(--hd)",fontSize:".9rem",fontWeight:700,letterSpacing:".1em",cursor:!endpoint.trim()?"not-allowed":"pointer"}}>
            {executing?"⏳ SENDING SIGNAL...":"📡 SEND TO MT5 / SAGEMASTER"}
          </button>
        </>}

        {step===3&&<>
          <div style={{padding:"20px",textAlign:"center",background:execResult?.ok?"#020c04":"#1a0308",border:`1px solid ${execResult?.ok?"#0d3320":"#5a0315"}`,borderRadius:10,marginBottom:18}}>
            <div style={{fontSize:"2rem",marginBottom:10}}>{execResult?.ok?"✅":"❌"}</div>
            <div style={{fontFamily:"var(--hd)",fontSize:"1rem",fontWeight:700,color:execResult?.ok?"#00d68f":"#ff4757",letterSpacing:".06em",marginBottom:8}}>{execResult?.ok?"SIGNAL SENT SUCCESSFULLY":"SEND FAILED"}</div>
            <div style={{fontSize:".78rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.7}}>{execResult?.msg}</div>
          </div>
          {execResult?.ok&&<div style={{fontSize:".72rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.8,background:"var(--surf)",borderRadius:8,padding:"12px 14px",marginBottom:16}}>
            ✔ Your MT5/SageMaster should now process the signal<br/>
            ✔ Verify the trade opened in your platform<br/>
            ✔ Confirm SL and TP levels are set correctly
          </div>}
          <button onClick={onClose} style={{width:"100%",padding:"12px",background:"transparent",border:"1px solid var(--border)",borderRadius:7,color:"var(--muted)",fontFamily:"var(--hd)",fontSize:".85rem",fontWeight:700,cursor:"pointer"}}>CLOSE</button>
        </>}
      </div>
    </div>
  );
}

// ── MT5 Execution Preview (order-type classification + validation) ─────────────
const MT5_ORDER_OPTS = [
  { id:"MARKET_BUY",  label:"MARKET BUY",  order_type:"MARKET",  direction:"BUY"  },
  { id:"MARKET_SELL", label:"MARKET SELL", order_type:"MARKET",  direction:"SELL" },
  { id:"BUY_LIMIT",   label:"BUY LIMIT",   order_type:"BUY_LIMIT",  direction:"BUY"  },
  { id:"SELL_LIMIT",  label:"SELL LIMIT",  order_type:"SELL_LIMIT", direction:"SELL" },
  { id:"BUY_STOP",    label:"BUY STOP",    order_type:"BUY_STOP",   direction:"BUY"  },
  { id:"SELL_STOP",   label:"SELL STOP",   order_type:"SELL_STOP",  direction:"SELL" },
  { id:"BUY_STOP_LIMIT",  label:"BUY STOP LIMIT",  order_type:"BUY_STOP_LIMIT",  direction:"BUY"  },
  { id:"SELL_STOP_LIMIT", label:"SELL STOP LIMIT", order_type:"SELL_STOP_LIMIT", direction:"SELL" },
];
const MT5_ORDER_HELP = {
  MARKET:"Executes immediately at the current market price.",
  BUY_LIMIT:"Buy BELOW current price — waits for a pullback down to entry.",
  SELL_LIMIT:"Sell ABOVE current price — waits for a rally up to entry.",
  BUY_STOP:"Buy ABOVE current price — triggers on an upside breakout.",
  SELL_STOP:"Sell BELOW current price — triggers on a downside breakdown.",
  BUY_STOP_LIMIT:"Once price hits the trigger (above market), a buy-limit is placed at entry.",
  SELL_STOP_LIMIT:"Once price hits the trigger (below market), a sell-limit is placed at entry.",
};
const MT5_BRIDGE_ASSETS = ["XAUUSD","BTCUSD"];

async function mt5Api(path, opts={}){
  const token = localStorage.getItem("flexx_access_token");
  const headers = { "Content-Type":"application/json" };
  if(token) headers["Authorization"] = `Bearer ${token}`;
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${path}`, { credentials:"include", headers, ...opts });
  const data = await r.json().catch(()=>({}));
  if(!r.ok) throw new Error(typeof data.detail === "string" ? data.detail : `Request failed (${r.status})`);
  return data;
}

function Mt5ExecutionPreview({sig, asset, onOrderType}){
  const isSell = String(sig.DIRECTION).toUpperCase()==="SELL";
  const refEntry = isSell ? parseFloat(sig.ENTRY_HIGH) : parseFloat(sig.ENTRY_LOW);
  const [otId,setOtId] = useState(isSell?"SELL_LIMIT":"BUY_LIMIT");
  const [trigger,setTrigger] = useState("");
  const [entry,setEntry] = useState(Number.isFinite(refEntry)?String(refEntry):"");
  const [val,setVal] = useState(null);
  const [busy,setBusy] = useState(false);
  const [execResult,setExecResult] = useState(null);
  const [err,setErr] = useState("");
  const opt = MT5_ORDER_OPTS.find(o=>o.id===otId) || MT5_ORDER_OPTS[0];
  const isStopLimit = otId.endsWith("STOP_LIMIT");
  const isMarket = opt.order_type==="MARKET";
  const hub = useIntelHubOptional();

  // Publish the parsed MT5 Setup signal to the Intel Hub so the AssetChart
  // Signal Ladder can display it. Additive — reads the already-parsed `sig`.
  useEffect(()=>{
    if(!hub || !sig) return;
    hub.setActiveSignal({
      symbol: asset,
      direction: String(sig.DIRECTION||"").toUpperCase(),
      entry: Number.isFinite(refEntry) ? refEntry : null,
      stop_loss: sig.STOP_LOSS!=null ? parseFloat(sig.STOP_LOSS) : null,
      tp1: sig.TP1!=null ? parseFloat(sig.TP1) : null,
      tp2: sig.TP2!=null ? parseFloat(sig.TP2) : null,
      tp3: sig.TP3!=null ? parseFloat(sig.TP3) : null,
      source: "mt5_setup",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sig, asset]);

  useEffect(()=>{ (async()=>{
    try{
      const v = await mt5Api("/mt5/validate",{method:"POST",body:JSON.stringify({symbol:asset,direction:sig.DIRECTION,entry:refEntry})});
      const sug = MT5_ORDER_OPTS.find(o=>o.order_type===v.suggested_order_type && o.direction===String(sig.DIRECTION).toUpperCase());
      if(sug) setOtId(sug.id);
    }catch(e){/* keep default */}
  })(); // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{ (async()=>{
    try{
      const v = await mt5Api("/mt5/validate",{method:"POST",body:JSON.stringify({
        symbol:asset, direction:opt.direction, entry: isMarket?null:parseFloat(entry),
        order_type: opt.order_type, trigger_price: isStopLimit?parseFloat(trigger)||null:null,
        stop_loss: sig.STOP_LOSS?parseFloat(sig.STOP_LOSS):null, tp1: sig.TP1?parseFloat(sig.TP1):null,
      })});
      setVal(v);
      if(onOrderType) onOrderType(opt.order_type, v.execution);
    }catch(e){ setVal({valid:false,message:e.message}); }
  })(); // eslint-disable-next-line react-hooks/exhaustive-deps
  },[otId,entry,trigger]);

  async function execute(){
    setBusy(true); setErr(""); setExecResult(null);
    try{
      const res = await mt5Api("/mt5/execute",{method:"POST",body:JSON.stringify({
        symbol:asset, direction:opt.direction, entry: isMarket?null:parseFloat(entry),
        stop_loss:parseFloat(sig.STOP_LOSS), tp1:parseFloat(sig.TP1),
        tp2:sig.TP2?parseFloat(sig.TP2):null, tp3:sig.TP3?parseFloat(sig.TP3):null,
        order_type:opt.order_type, trigger_price: isStopLimit?parseFloat(trigger)||null:null,
        confidence: sig.CONFIDENCE?parseInt(sig.CONFIDENCE):null, setup_type:sig.SETUP_TYPE||"",
      })});
      setExecResult(res);
    }catch(e){ setErr(e.message); }
    setBusy(false);
  }

  const canExecute = val && val.pass;
  const msgColor = !val ? "var(--muted)" : val.valid ? "#00d68f" : "#ff4757";
  const inp = { width:"100%", padding:"9px 11px", background:"#020608", border:"1px solid #1c2030",
    borderRadius:7, color:"var(--white)", fontFamily:"var(--mono)", fontSize:".85rem" };

  return (
    <div data-testid="mt5-exec-preview" style={{padding:"14px 15px",background:"#04070f",border:"1px solid #1c2c22",borderRadius:10,display:"grid",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{fontFamily:"var(--hd)",fontSize:".82rem",fontWeight:800,color:"#00d68f",letterSpacing:".08em"}}>⚡ MT5 EXECUTION — FLEXX BRIDGE</div>
        <span data-testid="mt5-exec-ordertype-badge" style={{fontFamily:"var(--mono)",fontSize:".72rem",fontWeight:700,color:"#00d68f",border:"1px solid #00d68f",borderRadius:5,padding:"3px 9px"}}>
          {opt.order_type} · {isMarket?"IMMEDIATE":"PENDING"}
        </span>
      </div>

      <div>
        <div style={{fontSize:".62rem",letterSpacing:".12em",color:"var(--muted)",marginBottom:6,textTransform:"uppercase"}}>Order Type <span style={{color:"#ff4757"}}>*</span></div>
        <select data-testid="mt5-exec-ordertype-select" value={otId} onChange={e=>{setOtId(e.target.value);setExecResult(null);}} style={inp}>
          {MT5_ORDER_OPTS.map(o=><option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
        <div style={{fontSize:".68rem",color:"var(--muted)",fontFamily:"var(--bd)",marginTop:5,lineHeight:1.5}}>{MT5_ORDER_HELP[opt.order_type]}</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns: isStopLimit?"1fr 1fr":"1fr",gap:10}}>
        {!isMarket && (
          <div>
            <div style={{fontSize:".62rem",color:"var(--muted)",marginBottom:5}}>ENTRY / LIMIT PRICE</div>
            <input data-testid="mt5-exec-entry" value={entry} onChange={e=>{setEntry(e.target.value);setExecResult(null);}} style={inp}/>
          </div>
        )}
        {isStopLimit && (
          <div>
            <div style={{fontSize:".62rem",color:"var(--muted)",marginBottom:5}}>TRIGGER (STOP) PRICE <span style={{color:"#ff4757"}}>*</span></div>
            <input data-testid="mt5-exec-trigger" value={trigger} onChange={e=>{setTrigger(e.target.value);setExecResult(null);}} placeholder="activation price" style={inp}/>
          </div>
        )}
      </div>

      <div data-testid="mt5-exec-validation" style={{fontFamily:"var(--mono)",fontSize:".76rem",color:msgColor,display:"flex",alignItems:"center",gap:7}}>
        <span>{!val?"…":val.valid?"✓":"✕"}</span>
        <span>{val?val.message:"Checking price rules…"}</span>
        {val&&val.current_price?<span style={{color:"var(--muted)",fontSize:".68rem"}}>· live {val.current_price}</span>:null}
      </div>

      {val&&(
        <div data-testid="mt5-exec-verdict" style={{display:"grid",gap:8,padding:"10px 12px",background:"#020608",border:`1px solid ${canExecute?"#0d3320":"#3a1520"}`,borderRadius:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <span style={{fontSize:".62rem",letterSpacing:".12em",color:"var(--muted)",textTransform:"uppercase"}}>Pre-trade check</span>
            <span data-testid="mt5-exec-passblock" style={{fontFamily:"var(--hd)",fontSize:".78rem",fontWeight:800,letterSpacing:".08em",color:canExecute?"#00d68f":"#ff4757",border:`1.5px solid ${canExecute?"#00d68f":"#ff4757"}`,borderRadius:5,padding:"3px 12px"}}>
              {canExecute?"✓ PASS":"⛔ BLOCK"}
            </span>
          </div>
          {!canExecute&&val.block_reasons&&val.block_reasons.length>0&&(
            <ul data-testid="mt5-exec-block-reasons" style={{margin:0,paddingLeft:16,color:"#ff8095",fontSize:".72rem",fontFamily:"var(--bd)",lineHeight:1.7}}>
              {val.block_reasons.map((r,i)=><li key={r}>{r}</li>)}
            </ul>
          )}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(90px, 1fr))",gap:8,fontFamily:"var(--mono)",fontSize:".7rem"}}>
            <div><div style={{color:"var(--muted)",fontSize:".58rem",letterSpacing:".08em"}}>RISK MODE</div><div style={{color:"var(--white)"}}>{(val.risk_mode||"fixed_lot")==="fixed_lot"?"Fixed lot":"Risk %"}</div></div>
            <div><div style={{color:"var(--muted)",fontSize:".58rem",letterSpacing:".08em"}}>LOT SIZE</div><div style={{color:"var(--white)"}}>{val.lot_size!=null?val.lot_size:"auto (risk %)"}</div></div>
            <div><div style={{color:"var(--muted)",fontSize:".58rem",letterSpacing:".08em"}}>STATUS</div><div style={{color:canExecute?"#00d68f":"#ff4757"}}>{canExecute?(isMarket?"IMMEDIATE":"PENDING"):"BLOCKED"}</div></div>
          </div>
        </div>
      )}

      <pre data-testid="mt5-exec-payload" style={{margin:0,padding:"10px 12px",background:"#020608",border:"1px solid #1c2030",borderRadius:7,color:"#9fb0c3",fontFamily:"var(--mono)",fontSize:".72rem",lineHeight:1.7,overflowX:"auto"}}>
{`SYMBOL: ${asset}
ORDER_TYPE: ${opt.order_type}
DIRECTION: ${opt.direction}
${isMarket?"EXECUTION: IMMEDIATE":`ENTRY_PRICE: ${entry||"—"}
EXECUTION: PENDING`}${isStopLimit?`\nTRIGGER: ${trigger||"—"}`:""}
SL: ${sig.STOP_LOSS}
TP1: ${sig.TP1}${sig.TP2?`\nTP2: ${sig.TP2}`:""}${sig.TP3?`\nTP3: ${sig.TP3}`:""}`}
      </pre>

      {err&&<div data-testid="mt5-exec-error" style={{color:"#ff4757",fontSize:".76rem"}}>{err}</div>}
      {execResult?(
        <div data-testid="mt5-exec-success" style={{padding:"10px 12px",background:"#031a0e",border:"1px solid #0d3320",borderRadius:7,color:"#00d68f",fontSize:".78rem",fontFamily:"var(--bd)"}}>
          ✓ Queued to your MT5 bridge — <b>{execResult.order_type}</b> · status <b>{execResult.status}</b>. Track it in Profile → MT5 Connection.
        </div>
      ):(
        <button data-testid="mt5-exec-btn" onClick={execute} disabled={!canExecute||busy}
          style={{width:"100%",padding:"13px",background: canExecute?"#00d68f":"transparent",border:`1.5px solid ${canExecute?"#00d68f":"#2a3040"}`,borderRadius:8,
            color: canExecute?"#04070f":"var(--muted)",fontFamily:"var(--hd)",fontSize:".85rem",fontWeight:800,letterSpacing:".1em",cursor:canExecute?"pointer":"not-allowed",opacity:busy?.6:1}}>
          {busy?"QUEUEING…":canExecute?"⚡ EXECUTE MT5":"⛔ BLOCKED"}
        </button>
      )}
      <div style={{fontSize:".62rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.5}}>
        Routes to <b>your</b> FlexxAxelBridge queue. Requires MT5 connected in Profile. Invalid order/price combinations are blocked before they reach MT5.
      </div>
    </div>
  );
}


// ── CFD Panel ─────────────────────────────────────────────────────────────────
function CfdPanel({asset, mode, pmirAccess, sageMasterEnabled, autoRunPayload, onAutoRunClear}){
  const [loading,setLoading]=useState(false);
  const [result,setResult]=useState("");
  const [bias,setBias]=useState(null);
  const [conf,setConf]=useState(null);
  const [err,setErr]=useState("");
  const [ctx,setCtx]=useState("");
  const [phase,setPhase]=useState("");
  const [signal,setSignal]=useState(null);
  const [tgCopied,setTgCopied]=useState(false);
  const [pmirCopied,setPmirCopied]=useState(false);
  const [showExecute,setShowExecute]=useState(false);
  const [execOt,setExecOt]=useState(null);
  const [anchor,setAnchor]=useState(null);
  const ref=useRef(null);
  const autoRanRef=useRef(false);

  // Auto-run when a fullAnalysisPayload handoff is received
  useEffect(()=>{
    if(!autoRunPayload||autoRanRef.current||loading||result) return;
    autoRanRef.current=true;
    if(onAutoRunClear) onAutoRunClear();
    runWith(autoRunPayload.systemPrompt, autoRunPayload.userMessage);
   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[autoRunPayload]);

  async function runWith(sysPrompt, usrMsg){
    setLoading(true);setResult("");setBias(null);setConf(null);setErr("");setPhase("searching");
    setTimeout(()=>setPhase("synthesizing"),4500);
    try{
      const res=await callAxel(sysPrompt||mode.getPrompt(asset), usrMsg||`Generate a complete ${mode.label} for ${asset}. Search live data first.`, asset);
      const txt=res?.text;
      if(txt){
        setResult(txt);setBias(getBias(txt));setConf(getConf(txt));setAnchor(res?.live_anchor||null);
        if(mode.id==="mt5"){ const s=parseSignal(txt); if(s) setSignal(s); }
        setTimeout(()=>ref.current?.scrollIntoView({behavior:"smooth",block:"start"}),100);
      } else setErr("No response. Try again.");
    }catch(e){ setErr(e.message||"Connection error. Please retry."); }
    setLoading(false);setPhase("");
  }

  async function run(){
    if(loading) return;
    await runWith(mode.getPrompt(asset),`Generate a complete ${mode.label} for ${asset}.${ctx.trim()?` Trader context: ${ctx.trim()}`:""}  Search live data first.`);
  }

  function reset(){ setResult("");setBias(null);setConf(null);setErr("");setCtx("");setSignal(null);setTgCopied(false);setPmirCopied(false);setShowExecute(false);setExecOt(null);setAnchor(null); }

  function copyTelegram(){ navigator.clipboard?.writeText(formatTelegram(signal)); setTgCopied(true); setTimeout(()=>setTgCopied(false),2500); }
  function copyPMIR(){ navigator.clipboard?.writeText(formatPMIR(signal)); setPmirCopied(true); setTimeout(()=>setPmirCopied(false),2500); }

  if(loading) return <LoadingView ticker={asset} phase={phase} labels={["💹 LIVE PRICE","📰 NEWS","📐 LEVELS","🔮 PATTERNS","📊 SIGNALS"]}/>;

  if(result) return (
    <div ref={ref}>
      {showExecute&&signal&&<ExecuteModal sig={signal} asset={asset} onClose={()=>setShowExecute(false)}/>}
      <ResultView ticker={asset} bias={bias} conf={conf} result={signal ? stripAiDistanceSection(result) : result} modeLabel={`${mode.icon} ${mode.label} · CFD Desk`} onReset={reset} onNew={()=>{setResult("");setBias(null);setConf(null);setSignal(null);}}/>
      {anchor&&(
        <div data-testid="mt5-live-anchor-stamp" style={{marginTop:10,display:"flex",alignItems:"center",gap:8,padding:"9px 13px",background:"#04120a",border:"1px solid #0d3320",borderRadius:8,fontFamily:"var(--mono)",fontSize:".76rem",color:"#00d68f",flexWrap:"wrap"}}>
          <span style={{display:"inline-block",width:8,height:8,borderRadius:"50%",background:"#00d68f",boxShadow:"0 0 6px #00d68f"}}/>
          <span style={{fontWeight:700}}>LIVE · {anchor.symbol}</span>
          <span style={{color:"var(--white)",fontWeight:700}}>{Number(anchor.price).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
          <span style={{color:"var(--muted)"}}>· as of {anchor.at} · real-time feed</span>
        </div>
      )}
      {mode.id==="mt5"&&signal&&(
        <div style={{marginTop:14,display:"flex",flexDirection:"column",gap:10}}>
          <TradeChart sig={signal}/>
          <DistanceBreakdown sig={signal}/>
          <div style={{padding:"10px 13px",background:"#020c04",border:"1px solid #0d3320",borderRadius:8,fontSize:".72rem",color:"#4a8a6a",fontFamily:"var(--bd)",lineHeight:1.7,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span>✅ Signal parsed: <b style={{color:"#00d68f"}}>{signal.SYMBOL} {signal.DIRECTION}</b> · Entry {signal.ENTRY_LOW}–{signal.ENTRY_HIGH} · SL {signal.STOP_LOSS} · TP1 {signal.TP1}</span>
            {execOt && <span data-testid="mt5-summary-ordertype" style={{fontFamily:"var(--mono)",fontSize:".68rem",fontWeight:700,color:"#00d68f",border:"1px solid #00d68f",borderRadius:4,padding:"2px 7px"}}>{execOt}</span>}
          </div>
          {MT5_BRIDGE_ASSETS.includes(asset) && (
            <Mt5ExecutionPreview sig={signal} asset={asset} onOrderType={(ot)=>setExecOt(ot)} />
          )}
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <button onClick={copyTelegram}
              style={{flex:1,minWidth:130,padding:"11px 8px",background:"transparent",border:"1.5px solid #2AABEE",borderRadius:8,color:"#2AABEE",fontFamily:"var(--hd)",fontSize:".78rem",fontWeight:700,letterSpacing:".06em",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
              {tgCopied?"✓ COPIED":"📱 FLEXX TELEGRAM"}
            </button>
            {/* PMIR button: only visible to users with pmir_access — PMIRGate removed */}
            {pmirAccess && (
              <button onClick={copyPMIR} data-testid="pmir-format-btn"
                style={{flex:1,minWidth:130,padding:"11px 8px",background:"transparent",border:"1.5px solid #38bdf8",borderRadius:8,color:"#38bdf8",fontFamily:"var(--hd)",fontSize:".78rem",fontWeight:700,letterSpacing:".06em",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                {pmirCopied?"✓ COPIED":"🔓 PMIR FORMAT"}
              </button>
            )}
            {!MT5_BRIDGE_ASSETS.includes(asset) && (
              <button onClick={()=>setShowExecute(true)}
                style={{flex:1,minWidth:130,padding:"11px 8px",background:"transparent",border:"1.5px solid #00d68f",borderRadius:8,color:"#00d68f",fontFamily:"var(--hd)",fontSize:".78rem",fontWeight:700,letterSpacing:".06em",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                ⚡ EXECUTE (EXTERNAL)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fu">
      {err&&<div style={{padding:"11px 13px",background:"#1a0810",border:"1px solid #5a1030",borderRadius:7,marginBottom:13,color:"#ff6b88",fontSize:".8rem"}}>{err}</div>}
      <div style={{padding:"13px 15px",background:`${mode.color}0d`,border:`1px solid ${mode.color}30`,borderRadius:8,marginBottom:16}}>
        <div style={{fontFamily:"var(--hd)",fontSize:".9rem",fontWeight:700,color:"var(--white)",marginBottom:4}}>{mode.icon} {mode.label} — {asset}</div>
        <div style={{fontSize:".76rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.6}}>
          {mode.id==="session"&&"Real-time directional bias for the current trading session — live price, news & technicals."}
          {mode.id==="mt5"&&"Complete MT5-ready trade plan: entry, stop, targets, RR ratios, and lot size guide."}
          {mode.id==="sm"&&(sageMasterEnabled?"SageMaster ecosystem analysis active — HELIX harmonics, AI Grid, AI DCA, and Omni Assist.":"SageMaster mode is OFF. Toggle ON in the tab bar to activate.")}
          {mode.id==="mtf"&&`Multi-timeframe directional bias for ${asset} — D1 through M5.`}
          {mode.id==="drivers"&&`What's actually driving ${asset} price right now — news, fundamentals, and upcoming catalysts.`}
        </div>
      </div>
      {mode.id==="sm"&&!sageMasterEnabled?(
        <div style={{padding:"32px",textAlign:"center",border:"1px dashed var(--border)",borderRadius:8}}>
          <div style={{fontSize:"1.4rem",marginBottom:8}}>🔮</div>
          <div style={{fontFamily:"var(--hd)",fontSize:".95rem",color:"#38bdf8",letterSpacing:".06em",marginBottom:6}}>SAGEMASTER MODE</div>
          <div style={{fontSize:".75rem",color:"var(--muted)",fontFamily:"var(--bd)",lineHeight:1.7}}>SageMaster requires explicit activation.<br/>Use the toggle in the tab bar above to enable it.</div>
        </div>
      ):<>
        <div style={{marginBottom:6,fontSize:".63rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase"}}>Trader context <span style={{color:"#2a3040"}}>(optional)</span></div>
        <textarea value={ctx} onChange={e=>setCtx(e.target.value)} placeholder={`e.g. currently watching ${asset} for long re-entry...`} rows={2}
          style={{width:"100%",padding:"10px 13px",marginBottom:16,background:"var(--surf)",border:"1.5px solid var(--border)",borderRadius:7,color:"var(--text)",fontFamily:"var(--bd)",fontSize:".83rem",resize:"none",lineHeight:1.65}}
          onFocus={e=>e.target.style.borderColor=mode.color} onBlur={e=>e.target.style.borderColor="var(--border)"}/>
        <button className="runbtn" onClick={run} data-testid="cfd-run-btn"
          style={{width:"100%",padding:"14px",background:"transparent",border:`1.5px solid ${mode.color}`,borderRadius:7,color:mode.color,fontSize:".95rem",fontWeight:700,letterSpacing:".12em",textTransform:"uppercase"}}>
          ⚡ RUN {asset} {mode.label.toUpperCase()}
        </button>
        <div style={{marginTop:8,textAlign:"center",fontSize:".62rem",color:"var(--muted)",fontFamily:"var(--bd)"}}>AXEL searches live {asset} data before every analysis</div>
      </>}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AxelResearchDesk({ pmirAccess = false, onFullAnalysis, fullAnalysisPayload, onFullAnalysisClear }) {
  const [section,setSection]=useState("cfd");
  const [rMode,setRMode]=useState("bias");
  const [cfdAsset,setCfdAsset]=useState("XAUUSD");
  const [cfdMode,setCfdMode]=useState("session");
  // SageMaster is OFF by default (RULE 6 — on-demand only)
  const [sageMasterEnabled,setSageMasterEnabled]=useState(false);
  const [autoRunPayload,setAutoRunPayload]=useState(null);

  // Handle Full Analysis handoff from Daily Brief
  useEffect(() => {
    if (!fullAnalysisPayload) return;
    const { asset } = fullAnalysisPayload;
    const CFD_ASSETS = ["XAUUSD", "BTCUSD", "USOIL", "UKOIL"];
    if (CFD_ASSETS.includes(asset)) {
      setSection("cfd");
      setCfdAsset(asset);
      setCfdMode("session");
      setAutoRunPayload(fullAnalysisPayload);
    } else {
      setSection("research");
      setRMode("bias");
      setAutoRunPayload(fullAnalysisPayload);
    }
    if (onFullAnalysisClear) onFullAnalysisClear();
  }, [fullAnalysisPayload, onFullAnalysisClear]);

  const currentR=R_MODES.find(m=>m.id===rMode);
  const currentCfd=CFD_MODES.find(m=>m.id===cfdMode);

  return(
    <div style={{minHeight:"100vh",background:"var(--bg)",paddingBottom:20}}>
      <style>{CSS}</style>

      <div style={{maxWidth:820,margin:"0 auto",padding:"12px 14px 0"}}>

        {/* Section switcher */}
        <div style={{display:"flex",gap:8,marginBottom:20}}>
          {[
            {id:"cfd",label:"🥇₿🛢 CFD DESK",sub:"XAUUSD · BTCUSD · USOIL · UKOIL · MT5 · SageMaster"},
            {id:"research",label:"🔍 RESEARCH DESK",sub:"Any Asset · Stocks · Forex · Crypto"}
          ].map(s=>{
            const active=section===s.id;
            return(
              <button key={s.id} onClick={()=>setSection(s.id)} data-testid={`research-section-${s.id}`}
                style={{flex:1,padding:"11px 10px",border:`1.5px solid ${active?"var(--gold)":"var(--border)"}`,borderRadius:9,background:active?"rgba(245,166,35,0.07)":"var(--surf)",cursor:"pointer",textAlign:"center",transition:"all .18s"}}>
                <div style={{fontFamily:"var(--hd)",fontSize:".9rem",fontWeight:800,color:active?"var(--gold)":"var(--muted)",letterSpacing:".05em"}}>{s.label}</div>
                <div style={{fontSize:".6rem",color:active?"#8a6020":"var(--muted)",marginTop:3,fontFamily:"var(--bd)"}}>{s.sub}</div>
              </button>
            );
          })}
        </div>

        {/* CFD Desk */}
        {section==="cfd"&&(
          <div className="fu">
            <div style={{marginBottom:14}}>
              <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:8}}>Select Asset</div>
              <div style={{display:"flex",gap:8}}>
                {[{id:"XAUUSD",label:"🥇 XAUUSD",sub:"Gold · CFD · Futures",c:"#F5A623"},{id:"BTCUSD",label:"₿ BTCUSD",sub:"Bitcoin · CFD · Futures",c:"#F97316"},{id:"USOIL",label:"🛢 USOIL",sub:"Crude Oil · CFD · WTI",c:"#84cc16"},{id:"UKOIL",label:"🛢 UKOIL",sub:"Brent Crude · CFD · Futures",c:"#06b6d4"}].map(a=>{
                  const active=cfdAsset===a.id;
                  return(
                    <button key={a.id} onClick={()=>{setCfdAsset(a.id);setCfdMode("session");}} data-testid={`cfd-asset-${a.id}`}
                      style={{flex:1,padding:"12px 10px",border:`1.5px solid ${active?a.c:"var(--border)"}`,borderRadius:9,background:active?`${a.c}10`:"var(--surf)",cursor:"pointer",textAlign:"center",transition:"all .18s"}}>
                      <div style={{fontFamily:"var(--hd)",fontSize:"1rem",fontWeight:800,color:active?a.c:"var(--muted)",letterSpacing:".04em"}}>{a.label}</div>
                      <div style={{fontSize:".62rem",color:active?`${a.c}99`:"var(--muted)",marginTop:3,fontFamily:"var(--bd)"}}>{a.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CFD Mode tabs */}
            <div style={{marginBottom:18}}>
              <div style={{fontSize:".62rem",letterSpacing:".14em",color:"var(--muted)",textTransform:"uppercase",marginBottom:8}}>Analysis Mode</div>
              <div style={{display:"flex",gap:7,overflowX:"auto",paddingBottom:3,flexWrap:"wrap"}}>
                {CFD_MODES.map(m=>{
                  const active=cfdMode===m.id;
                  const isSM = m.id==="sm";
                  return(
                    <button key={m.id} className="tabx" data-testid={`cfd-mode-${m.id}`}
                      onClick={()=>{ setCfdMode(m.id); if(isSM) setSageMasterEnabled(v=>!v); }}
                      style={{padding:"8px 13px",border:`1px solid ${active?(isSM&&sageMasterEnabled?"#38bdf8":m.color):"var(--border)"}`,background:active?`${m.color}12`:"var(--surf)",color:active?m.color:"var(--muted)",fontWeight:700,fontSize:".78rem",letterSpacing:".06em",flexShrink:0,borderRadius:6,display:"flex",alignItems:"center",gap:5}}>
                      <span>{m.icon}</span><span>{m.label}</span>
                      {isSM&&<span style={{fontSize:".6rem",color:sageMasterEnabled?"#38bdf8":"var(--muted)",marginLeft:2}}>{sageMasterEnabled?"ON":"OFF"}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {cfdMode==="live"
              ? <LiveTriggerPanel key={`lt-${cfdAsset}`} asset={cfdAsset} />
              : <CfdPanel key={`${cfdAsset}-${cfdMode}`} asset={cfdAsset} mode={currentCfd} pmirAccess={pmirAccess} sageMasterEnabled={sageMasterEnabled} autoRunPayload={cfdMode==="session"?autoRunPayload:null} onAutoRunClear={()=>setAutoRunPayload(null)}/>
            }
          </div>
        )}

        {/* Research Desk */}
        {section==="research"&&(
          <div className="fu">
            <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,marginBottom:16,scrollbarWidth:"none",flexWrap:"wrap"}}>
              {R_MODES.map(m=>{
                const active=rMode===m.id;
                return(
                  <button key={m.id} className="tabx" data-testid={`research-mode-${m.id}`} onClick={()=>setRMode(m.id)}
                    style={{padding:"7px 12px",border:`1px solid ${active?m.color:"var(--border)"}`,background:active?`${m.color}14`:"var(--surf)",color:active?m.color:"var(--muted)",fontWeight:700,fontSize:".78rem",letterSpacing:".05em",flexShrink:0,display:"flex",alignItems:"center",gap:5,borderRadius:6}}>
                    <span>{m.icon}</span><span>{m.label}</span>
                  </button>
                );
              })}
            </div>
            <div style={{padding:"11px 13px",background:"var(--surf)",border:"1px solid var(--border)",borderRadius:8,marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontFamily:"var(--hd)",fontSize:".9rem",fontWeight:700,color:"var(--white)"}}>{currentR.icon} {currentR.label}</div>
                <div style={{fontSize:".72rem",color:"var(--muted)",marginTop:3,fontFamily:"var(--bd)"}}>Works on any stock, forex, crypto, index, or commodity</div>
              </div>
              <div style={{background:`${currentR.color}15`,border:`1px solid ${currentR.color}40`,color:currentR.color,padding:"3px 8px",borderRadius:4,fontSize:".6rem",fontFamily:"var(--hd)",letterSpacing:".1em",fontWeight:700,flexShrink:0}}>{currentR.tag}</div>
            </div>
            <ResearchPanel key={rMode} mode={currentR}/>
          </div>
        )}

        <div style={{marginTop:24,padding:"12px 14px",background:"#050710",border:"1px solid #1a2030",borderRadius:8,textAlign:"center",fontSize:".6rem",color:"#2a3545",letterSpacing:".1em",textTransform:"uppercase",lineHeight:2,fontFamily:"var(--hd)"}}>
          For educational purposes only. Not financial advice. Always manage your own risk.<br/>
          AXEL Research Intelligence · Flexx Academy · Powered by Claude AI + Live Web Search
        </div>
      </div>
    </div>
  );
}
